import { useCallback, useEffect, useRef, useState } from 'react';
import { endpoints } from '../../services/api';
import { errorRetorno, historicoValido, previewConfirmable, bindingVigente } from './retornosModel';

export default function useRetornos(expedienteId, onConfirmed) {
    const [historico, setHistorico] = useState(null);
    const [loading, setLoading] = useState(true);
    const [historyError, setHistoryError] = useState(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);
    const mounted = useRef(false);
    const previewSeq = useRef(0);
    const historySeq = useRef(0);
    const currentFile = useRef(null);
    const previewBinding = useRef(null);
    const confirming = useRef(false);

    const invalidate = useCallback(() => {
        previewSeq.current += 1;
        previewBinding.current = null;
        setPreview(null);
        setAcknowledged(false);
    }, []);

    const loadHistory = useCallback(async () => {
        const seq = ++historySeq.current;
        setLoading(true);
        setHistoryError(null);
        try {
            const { data } = await endpoints.n63.retornos(expedienteId);
            if (!historicoValido(data)) throw new Error('Respuesta de histórico incompleta. No se habilita la confirmación.');
            if (mounted.current && seq === historySeq.current) setHistorico(data);
        } catch (err) {
            if (mounted.current && seq === historySeq.current) {
                setHistorico(null);
                setHistoryError(errorRetorno(err));
                invalidate();
                setBusy(previous => previous === 'preview' ? '' : previous);
            }
        } finally {
            if (mounted.current && seq === historySeq.current) setLoading(false);
        }
    }, [expedienteId, invalidate]);

    useEffect(() => {
        mounted.current = true;
        loadHistory();
        return () => {
            mounted.current = false;
            previewSeq.current += 1;
            historySeq.current += 1;
        };
    }, [loadHistory]);

    const runPreview = async selected => {
        if (!selected || confirming.current) return;
        invalidate();
        const seq = previewSeq.current;
        setError(null);
        setNotice('');
        setBusy('preview');
        try {
            const { data } = await endpoints.n63.previewRetorno(expedienteId, selected);
            if (!previewConfirmable(data)) throw new Error('Preview inválido o incompleto. No se puede confirmar; solicita una nueva previsualización.');
            if (mounted.current && seq === previewSeq.current && currentFile.current === selected) {
                previewBinding.current = { file: selected, token: data.token, sequence: seq };
                setPreview(data);
            }
        } catch (err) {
            if (mounted.current && seq === previewSeq.current) {
                setPreview(null);
                setAcknowledged(false);
                setError(errorRetorno(err));
            }
        } finally {
            if (mounted.current && seq === previewSeq.current) setBusy('');
        }
    };

    const selectFile = selected => {
        if (confirming.current) return;
        invalidate();
        currentFile.current = selected || null;
        setFile(selected || null);
        setError(null);
        setNotice('');
        setBusy('');
        if (selected) runPreview(selected);
    };

    const canConfirm = Boolean(file && currentFile.current === file && previewConfirmable(preview)
        && bindingVigente(previewBinding.current, file, preview, previewSeq.current)
        && acknowledged && historico && !loading && !historyError && !busy);

    const confirmPreview = async () => {
        if (!canConfirm || confirming.current || currentFile.current !== file
            || !bindingVigente(previewBinding.current, file, preview, previewSeq.current)) return;
        confirming.current = true;
        setBusy('confirm');
        setError(null);
        // The exact File reference and the server-issued token are submitted together.
        const selected = currentFile.current;
        const token = preview.token;
        try {
            const { data } = await endpoints.n63.confirmarRetorno(expedienteId, selected, token);
            if (!data?.id) throw new Error('Confirmación sin identificador de recepción. Comprueba el histórico antes de reintentar.');
            if (!mounted.current) return;
            invalidate();
            currentFile.current = null;
            setFile(null);
            setNotice(data.duplicado
                ? 'Original ya recibido: reimportación idempotente, sin crear otra recepción ni reemplazar evidencias.'
                : `Recepción #${data.id} confirmada. Original conservado en el histórico inmutable.`);
            await loadHistory();
            if (onConfirmed && mounted.current) {
                try { await onConfirmed(); }
                catch { if (mounted.current) setNotice(previous => `${previous} No se pudo actualizar la cabecera del expediente.`); }
            }
        } catch (err) {
            if (mounted.current) {
                invalidate();
                setError(errorRetorno(err));
                // A timeout may have committed: recover read-only history, never auto-retry confirmation.
                await loadHistory();
            }
        } finally {
            confirming.current = false;
            if (mounted.current) setBusy('');
        }
    };

    const refresh = () => {
        if (confirming.current) return;
        invalidate();
        setBusy('');
        loadHistory();
    };

    return { historico, loading, historyError, file, preview, busy, error, notice, acknowledged,
        setAcknowledged, selectFile, runPreview, canConfirm, confirmPreview, refresh };
}
